import type { DashboardSenderTransferRecord } from "@/api/custom_types";
import { AdminLink } from "@/components/base";
import { useApiError } from "@/hooks";
import { usePersistedServerTableState } from "@/hooks/usePersistedServerTableState";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetSenderTransferRecord } from "@/services";
import { getTimeSinceText } from "@/utils/timestamp";
import { calculateTaxPercentage, formatNumber } from "@/utils/tools";
import {
	type StatusTranslation,
	getTransferRecordStatusTranslation,
} from "@/utils/translations";
import { Chip, type ChipVariant, DataTable } from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

const STATUS_COLOR_TO_CHIP_VARIANT: Record<
	StatusTranslation["color"],
	ChipVariant
> = {
	success: "success",
	primary: "info",
	error: "error",
	warning: "warning",
};

interface SenderInfoTransactionsProps {
	senderId: string;
}

const SenderInfoTransactions: React.FC<SenderInfoTransactionsProps> = ({
	senderId,
}) => {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();

	const server = usePersistedServerTableState(
		`sender-transactions:${senderId}`,
	);

	const {
		data: transferRecords,
		isLoading,
		isFetching,
		error: getSenderTransferRecordError,
		refetch,
	} = useGetSenderTransferRecord({
		query: {
			page: server.pageIndex + 1,
			page_size: server.pageSize,
		},
		path: {
			sender_id: senderId,
		},
	});

	const error = getSenderTransferRecordError
		? getErrorInterface(getSenderTransferRecordError)
		: null;

	const columns = useMemo<ColumnDef<DashboardSenderTransferRecord>[]>(
		() => [
			{
				id: "player_info",
				header: t("senderInfo.transactions.tablePlayer"),
				enableSorting: false,
				cell: ({ row }) => {
					const player = row.original.player_metadata;
					if (!player) return "-";
					const name = player.nick_name || player.full_name || "-";
					const rating =
						player.rating !== undefined && player.rating !== null
							? player.rating
							: "-";
					const rarity = player.rarity_name || "-";
					return (
						<AdminLink
							to="/st-admin/transactions"
							search={{ players: String(player.resource_id) }}
						>
							{`${name} ${rating} ${rarity}`}
						</AdminLink>
					);
				},
			},
			{
				id: "order_email",
				header: t("senderInfo.transactions.tableOrderEmail"),
				enableSorting: false,
				cell: ({ row }) => (
					<AdminLink
						to="/st-admin/transactions"
						search={{ orderEmail: row.original.order_email }}
					>
						{row.original.order_email}
					</AdminLink>
				),
			},
			{
				id: "commission_fee",
				header: t("senderInfo.transactions.tableCommissionFee"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.commission_fee, "price"),
			},
			{
				id: "sender_ping",
				header: t("senderInfo.transactions.tableSenderPing"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.sender_ping),
			},
			{
				id: "taken",
				header: t("senderInfo.transactions.tableTaken"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.taken),
			},
			{
				id: "delivered",
				header: t("senderInfo.transactions.tableDelivered"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.delivered),
			},
			{
				id: "tax",
				header: t("order.tableTax"),
				enableSorting: false,
				cell: ({ row }) => {
					const { taken, delivered } = row.original;
					const tax = calculateTaxPercentage(delivered, taken);

					return formatNumber(tax, "percent");
				},
			},
			{
				id: "state",
				header: t("senderInfo.transactions.tableStatus"),
				enableSorting: false,
				cell: ({ row }) => {
					const { color, text } = getTransferRecordStatusTranslation(
						row.original.state,
						t,
					);
					return (
						<Chip variant={STATUS_COLOR_TO_CHIP_VARIANT[color]} size="sm">
							{text}
						</Chip>
					);
				},
			},
			{
				id: "created_at",
				header: t("order.tableDate"),
				enableSorting: false,
				cell: ({ row }) =>
					(row.original.created_at &&
						getTimeSinceText(row.original.created_at, t, lang)) ||
					"-",
			},
		],
		[t, lang],
	);

	return (
		<DataTable
			columns={columns}
			data={transferRecords?.results ?? []}
			totalItems={transferRecords?.count ?? 0}
			server={server}
			isLoading={isLoading}
			isFetching={isFetching}
			error={error}
			onRetry={refetch}
			getRowId={(row) => row.id}
		/>
	);
};

export default SenderInfoTransactions;
